import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Clock,
  User,
  Plus,
  Edit,
  ArrowRight,
  Archive,
  UserPlus,
  UserMinus,
  UserCheck,
  UserX,
  ChevronDown,
  ChevronUp,
  History,
  ExternalLink,
} from "lucide-react";
import {
  JOB_ACTIVITY_CONFIG,
  JobActivityItem,
  JobActivityType,
  FIELD_LABELS,
} from "../constants/jobActivityConfig";
import "./JobActivityTimeline.css";

interface JobActivityTimelineProps {
  activities: JobActivityItem[];
  isLoading?: boolean;
  variant?: "full" | "summary";
  jobId?: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
}

const ICON_COMPONENTS: Record<string, React.ReactNode> = {
  Plus: <Plus size={16} />,
  Edit: <Edit size={16} />,
  ArrowRight: <ArrowRight size={16} />,
  Archive: <Archive size={16} />,
  UserPlus: <UserPlus size={16} />,
  UserMinus: <UserMinus size={16} />,
  UserCheck: <UserCheck size={16} />,
  UserX: <UserX size={16} />,
};

const JobActivityTimeline: React.FC<JobActivityTimelineProps> = ({
  activities,
  isLoading = false,
  variant = "full",
  jobId,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateString);
  };

  const getActivityConfig = (type: JobActivityType) => {
    return (
      JOB_ACTIVITY_CONFIG[type] || {
        icon: "Edit",
        color: "#6b7280",
        label: type,
        cssClass: "activity-default",
      }
    );
  };

  const hasExpandableDetails = (activity: JobActivityItem) => {
    const { details } = activity;
    if (!details) return false;
    return (
      (details.fieldChanges && details.fieldChanges.length > 0) ||
      details.note ||
      details.archiveReasonNote
    );
  };

  const renderFieldChanges = (activity: JobActivityItem) => {
    const { fieldChanges } = activity.details;
    if (!fieldChanges || fieldChanges.length === 0) return null;

    return (
      <div className="field-changes">
        {fieldChanges.map((change, index) => (
          <div key={index} className="field-change-item">
            <span className="field-name">
              {FIELD_LABELS[change.field] || change.field}:
            </span>
            <span className="field-old">
              {formatFieldValue(change.oldValue)}
            </span>
            <ArrowRight size={12} className="change-arrow" />
            <span className="field-new">
              {formatFieldValue(change.newValue)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const formatFieldValue = (value: unknown): string => {
    if (value === null || value === undefined) return "(empty)";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) {
      if (value.length === 0) return "(none)";
      return value.join(", ");
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };

  if (isLoading) {
    return (
      <div className="job-activity-loading">
        <div className="spinner"></div>
        <p>{t("common:loading.loading")}</p>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="job-activity-empty">
        <History size={48} />
        <p>{t("common:empty.noData")}</p>
      </div>
    );
  }

  return (
    <div className={`job-activity-timeline ${variant}`}>
      {activities.map((activity, index) => {
        const config = getActivityConfig(activity.type);
        const isExpanded = expandedItems.has(activity.id);
        const canExpand = hasExpandableDetails(activity);

        return (
          <div
            key={activity.id}
            className={`timeline-item ${config.cssClass}`}
            style={{ "--activity-color": config.color } as React.CSSProperties}
          >
            <div className="timeline-marker">
              <div
                className="marker-icon"
                style={{ backgroundColor: config.color }}
              >
                {ICON_COMPONENTS[config.icon] || <Edit size={16} />}
              </div>
              {index < activities.length - 1 && (
                <div className="marker-line"></div>
              )}
            </div>
            <div className="timeline-content">
              <div className="timeline-header">
                <h4 className="activity-title">{activity.description}</h4>
                <span
                  className="activity-date"
                  title={formatDate(activity.timestamp)}
                >
                  <Clock size={14} />
                  {variant === "summary"
                    ? formatRelativeDate(activity.timestamp)
                    : formatDate(activity.timestamp)}
                </span>
              </div>
              <div className="timeline-meta">
                <span className="actor-info">
                  <User size={14} />
                  {activity.user.name}
                </span>
                <span
                  className="activity-type-badge"
                  style={{
                    backgroundColor: `${config.color}20`,
                    color: config.color,
                  }}
                >
                  {config.label}
                </span>
              </div>

              {/* Expandable details */}
              {canExpand && (
                <button
                  className="expand-toggle"
                  onClick={() => toggleExpand(activity.id)}
                  aria-expanded={isExpanded}
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp size={14} /> {t("common:actions.showLess")}
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} /> {t("common:actions.showMore")}
                    </>
                  )}
                </button>
              )}

              {isExpanded && (
                <div className="timeline-details">
                  {renderFieldChanges(activity)}
                  {activity.details.note && (
                    <div className="detail-note">
                      <strong>Note:</strong> {activity.details.note}
                    </div>
                  )}
                  {activity.details.archiveReasonNote && (
                    <div className="detail-note">
                      <strong>Reason:</strong>{" "}
                      {activity.details.archiveReasonNote}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Summary mode: View all link */}
      {variant === "summary" && jobId && (
        <div className="view-all-link">
          <Link to={`/jobs/${jobId}/history`}>
            {t("common:actions.viewAll")} <ExternalLink size={14} />
          </Link>
        </div>
      )}

      {/* Full mode: Load more button */}
      {variant === "full" && hasMore && onLoadMore && (
        <div className="load-more-container">
          <button
            className="load-more-btn"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore
              ? t("common:loading.loading")
              : t("common:actions.showMore")}
          </button>
        </div>
      )}
    </div>
  );
};

export default JobActivityTimeline;
